/**
 * @file     ResultPeekBar.tsx
 * @purpose  Phone-width result bar for the family 7 tools. While the result
 *           card is off screen (the inputs come first on a phone), a glass bar
 *           at the bottom keeps the live figure in view and offers the primary
 *           action. It steps aside over the site footer and is inert while
 *           hidden. The kit StickyActionBar only reacts to a target scrolled
 *           above the fold; this result usually sits below it.
 */
'use client';

import { useEffect, useState } from 'react';
import { glassMaterial } from '@/design-system/tokens/glass';
import Link from 'next/link';
import Box from '@mui/material/Box';
import { layoutTokens } from '@/design-system/tokens/layout';
import { motionTokens } from '@/design-system/tokens/motion';
import { zLayers } from '@/design-system/tokens/zLayers';
import { focusRingSx, hubColors, hubRadius, microLabelSx } from '@/components/hub/hubKitStyles';

type ResultPeekBarProps = {
  targetId: string;
  label: string;
  value: string | null;
  actionLabel: string;
  actionHref: string;
};

export function ResultPeekBar({ targetId, label, value, actionLabel, actionHref }: ResultPeekBarProps) {
  const [targetInView, setTargetInView] = useState(true);
  const [footerInView, setFooterInView] = useState(false);

  useEffect(() => {
    const target = document.getElementById(targetId);
    if (!target || typeof IntersectionObserver === 'undefined') return;
    const targetObserver = new IntersectionObserver(([entry]) => {
      if (entry) setTargetInView(entry.isIntersecting);
    });
    targetObserver.observe(target);
    const footer = document.querySelector('footer');
    const footerObserver = footer
      ? new IntersectionObserver(([entry]) => {
          if (entry) setFooterInView(entry.isIntersecting);
        })
      : null;
    if (footer) footerObserver?.observe(footer);
    return () => {
      targetObserver.disconnect();
      footerObserver?.disconnect();
    };
  }, [targetId]);

  const visible = Boolean(value) && !targetInView && !footerInView;
  const motion = `${motionTokens.durations.normal}ms ${motionTokens.easing.standard}`;

  return (
    <Box
      role="region"
      aria-label={label}
      aria-hidden={!visible}
      inert={!visible}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: zLayers.stickyBar,
        display: { xs: 'block', md: 'none' },
        px: 5,
        pt: 1,
        pb: 'calc(10px + env(safe-area-inset-bottom, 0px))',
        ...glassMaterial('chrome', { edge: 'top' }),
        transform: visible ? 'translateY(0)' : 'translateY(110%)',
        opacity: visible ? 1 : 0,
        transition: `transform ${motion}, opacity ${motion}`,
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <Box sx={{ maxWidth: 680, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          component="a"
          href={`#${targetId}`}
          sx={{ flex: '1 1 auto', minWidth: 0, minHeight: 44, display: 'flex', flexDirection: 'column', justifyContent: 'center', textDecoration: 'none', color: 'inherit', borderRadius: `${hubRadius.control}px`, ...focusRingSx }}
        >
          <Box component="span" sx={microLabelSx}>{label}</Box>
          <Box component="span" sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums', color: hubColors.textPrimary }}>
            {value}
          </Box>
        </Box>
        <Box
          component={Link}
          href={actionHref}
          sx={{
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: 48,
            px: `${layoutTokens.button.md.paddingX}px`,
            borderRadius: `${hubRadius.control}px`,
            backgroundColor: hubColors.interactivePrimary,
            color: hubColors.textOnPrimary,
            fontSize: 15,
            fontWeight: 500,
            textDecoration: 'none',
            ...focusRingSx,
          }}
        >
          {actionLabel}
        </Box>
      </Box>
    </Box>
  );
}
